import {Loop} from '@mazechazer/redux-loop'
import {initLoginForm} from '../Components/LoginForm/LoginFormReducer'
import {navigate} from '../Data/Browser'
import {sendRequest} from '../Data/Http'
import * as HttpRequest from '../Data/HttpRequest'
import * as Location from '../Data/Location'
import {update} from '../Data/ObjectHelper'
import * as RoutedLocation from '../Data/RoutedLocation'
import {Session} from '../Data/Session'
import {timeout} from '../Data/Time'
import {Manufacturer} from '../Entities/Manufacturer.entity.evolve'
import * as InqiuryDetailsReducer
    from '../Pages/InquiryDetails/InquiryDetailsReducer'
import * as InquiryOverviewReducer
    from '../Pages/InquiryOverview/InquiryOverviewReducer'
import * as OrderCreateReducer from '../Pages/OrderCreate/OrderCreateReducer'
import * as OrderDetailsReducer from '../Pages/OrderDetails/OrderDetailsReducer'
import * as OrderExportCreateReducer
    from '../Pages/OrderExportCreate/OrderExportCreateReducer'
import * as OrderExportOverviewReducer
    from '../Pages/OrderExportOverview/OrderExportOverviewReducer'
import * as OrderOverviewReducer
    from '../Pages/OrderOverview/OrderOverviewReducer'
import * as PasswordChangeReducer
    from '../Pages/PasswordChange/PasswordChangeReducer'
import {initPasswordLostPage} from '../Pages/PasswordLost/PasswordLostReducer'
import * as PrismaRequestDetailsReducer
    from '../Pages/PrismaRequestDetails/PrismaRequestDetailsReducer'
import * as PrismaRequestOverviewReducer
    from '../Pages/PrismaRequestOverview/PrismaRequestOverviewReducer'
import * as ProductCalculationDetailsReducer
    from '../Pages/ProductCalculationDetails/ProductCalculationDetailsReducer'
import * as ProductCalculationImportReducer
    from '../Pages/ProductCalculationImport/ProductCalculationImportReducer'
import * as ProductCalculationOverviewReducer
    from '../Pages/ProductCalculationOverview/ProductCalculationOverviewReducer'
import {Action} from './Actions'
import * as AuthenticatedRoute from './AuthenticatedRoute'
import {reduceCreateOrderFeedback} from './FeedbackProcessing'
import {PageLoadIdentifier} from './PageLoadIdentifier'
import * as PublicRoute from './PublicRoute'
import {Authenticated, AuthenticatedPage, PublicPage, State} from './State'

export function run(
    session: Session | null,
    hash: string,
): Loop<State, Action> {
    const location = Location.fromHash(hash)
    if (session === null) {
        const pageLoop = initializePublicPage(location)
        return {
            state: {
                type: 'Public',
                page: pageLoop.state,
            },
            effects: pageLoop.effects,
        }
    } else {
        return dispatchNavigation(
            initAuthenticatedState(session, {type: 'AuthenticatedLoadingPage'}),
            location,
        )
    }
}

export function dispatchNavigation(
    state: Authenticated,
    location: Location.M,
): Loop<State, Action> {
    const {route, queryParameters} =
        RoutedLocation.fromLocation(AuthenticatedRoute.definitions, location)
    if (route !== null) {
        return handleAuthenticatedNavigation(state, {route, queryParameters})
    } else {
        return {
            state: update(
                {
                    pageLoad: {type: 'Default'},
                    page: {type: 'AuthenticatedNotFoundPage'},
                },
                state,
            ),
            effects: [],
        }
    }
}

function initAuthenticatedState(
    session: Session,
    page: AuthenticatedPage,
): Authenticated {
    return {
        type: 'Authenticated',
        relogin: null,
        session,
        page,
        pageLoad: {type: 'Loading'},
        runningPageLoads: 0,
        loadingIndicator: false,
        drawerOpen: false,
    }
}

export function initializePublicPage(
    location: Location.M,
): Loop<PublicPage, Action> {
    const {route} =
        RoutedLocation.fromLocation(PublicRoute.definitions, location)
    return route !== null ?
        ((): Loop<PublicPage, Action> => {
            switch (route.type) {
                case 'Root':
                case 'Login':
                    return {
                        state: {
                            type: 'LoginPage',
                            state: initLoginForm(''),
                        },
                        effects: []
                    }
                case 'PasswordLost':
                    return {
                        state: {
                            type: 'PasswordLostPage',
                            state: initPasswordLostPage(),
                        },
                        effects: [],
                    }
            }
        })() :
        {
            state: {
                type: 'PublicRedirectPage'
            },
            effects: [
                navigate<Action>(
                    PublicRoute.url({type: 'Login'}),
                    {type: 'NoOp'},
                ),
            ]
        }
}

export function handleAuthenticatedNavigation(
    state: Authenticated,
    routedLocation: RoutedLocation.M<AuthenticatedRoute.M>
): Loop<State, Action> {
    const {route, queryParameters} = routedLocation
    switch (route.type) {
        case 'Root':
            return handleAuthenticatedNavigation(
                state,
                update(
                    {route: rootRoute(state.session.manufacturer)},
                    routedLocation,
                ),
            )
        case 'OrderOverview': {
            const [httpRequest, pageSection] =
                OrderOverviewReducer.activate(state.session, queryParameters)
            return fetchResource(
                state,
                {type: 'OrderOverview', pageSection},
                httpRequest,
            )
        }
        case 'OrderDetails':
            return fetchResource(
                state,
                {type: 'OrderDetails'},
                OrderDetailsReducer.activate(
                    state.session,
                    route.orderNumber,
                ),
            )
        case 'InquiryOverview': {
            const [httpRequest, pageSection] =
                InquiryOverviewReducer.activate(state.session, queryParameters)
            return fetchResource(
                state,
                {type: 'InquiryOverview', pageSection},
                httpRequest,
            )
        }
        case 'InquiryDetails':
            return fetchResource(
                state,
                {type: 'InquiryDetails'},
                InqiuryDetailsReducer.activate(
                    state.session,
                    route.inquiryNumber,
                ),
            )
        case 'OrderCreate':
            const
                orderCreateLoop = OrderCreateReducer.init(),
                wrappedLoop = {
                    state: update(
                        {
                            page: {
                                type: 'OrderCreatePage',
                                state: orderCreateLoop.state,
                            },
                        },
                        state,
                    ),
                    effects: [],
                }
            return initializePage(
                reduceCreateOrderFeedback(
                    orderCreateLoop.feedback,
                    wrappedLoop,
                ),
            )
        case 'OrderExportCreate':
            return initializePage(
                {
                    state: update(
                        {
                            page: {
                                type: 'OrderExportCreatePage',
                                state: OrderExportCreateReducer.initWithoutTemplate()
                            },
                        },
                        state,
                    ),
                    effects: []
                }
            )
        case 'OrderExportCreateFromTemplate':
            return fetchResource(
                state,
                {type: 'OrderExportCreate'},
                OrderExportCreateReducer.activate(route.id),
            )
        case 'OrderExportOverview':
            return fetchResource(
                state,
                {type: 'OrderExportOverview'},
                OrderExportOverviewReducer.activate(state.session),
            )
        case 'PasswordChange':
            return initializePage(
                {
                    state: update(
                        {
                            page: {
                                type: 'PasswordChangePage',
                                state: PasswordChangeReducer.init(),
                            }
                        },
                        state,
                    ),
                    effects: [],
                }
            )
        case 'ProductCalculationOverview':
            return fetchResource(
                state,
                {type: 'ProductCalculationOverview'},
                ProductCalculationOverviewReducer.activate(state.session),
            )
        case 'ProductCalculationDetails':
            return fetchResource(
                state,
                {type: 'ProductCalculationDetails'},
                ProductCalculationDetailsReducer.activate(route.id),
            )
        case 'ProductCalculationImport':
            return fetchResource(
                state,
                {type: 'ProductCalculationImport'},
                ProductCalculationImportReducer.activate(state.session),
            )
        case 'PrismaRequestOverview':
            return fetchResource(
                state,
                {type: 'PrismaRequestOverview'},
                PrismaRequestOverviewReducer.activate(state.session),
            )
        case 'PrismaRequestDetails':
            return fetchResource(
                state,
                {type: 'PrismaRequestDetails'},
                PrismaRequestDetailsReducer.activate(state.session, route.id),
            )
    }
}

export function rootRoute(manufacturer: Manufacturer): AuthenticatedRoute.M {
    switch (manufacturer.id) {
        default:
            return {type: 'PrismaRequestOverview'}
    }
}

function fetchResource(
    state: Authenticated,
    identifier: PageLoadIdentifier,
    httpRequest: HttpRequest.HttpRequest,
): Loop<State, Action> {
    return {
        state: update(
            {
                pageLoad: {type: 'Loading'},
                runningPageLoads: state.runningPageLoads + 1,
            },
            state,
        ),
        effects: [
            sendRequest<Action>(
                result => ({
                    type: 'PageLoadResponse',
                    httpRequest,
                    identifier,
                    result,
                }),
                HttpRequest.authorize(
                    state.session.authentication.token,
                    httpRequest
                ),
            ),
            timeout<Action>(400, {type: 'PageLoadSoftTimeout'}),
        ],
    }
}

function initializePage(
    {state, effects}: Loop<State, Action>
): Loop<State, Action> {
    return {
        state: update({pageLoad: {type: 'Default'}}, state),
        effects,
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/App/Routing.ts